/**
 * Nike application
 * @author {Cognizant Technology Solutions}
 */
import { OktaAuth } from '@okta/okta-auth-js';
import '@okta/okta-auth-js/polyfill';

const oktaSymbol = Symbol('okta');
const config = {
  qa: {
    // DEV || NONPROD7 || PLAYGROUND
    issuer: process.env.REACT_APP_DPOM_OKTA_NONPROD,
    authorizeUrl: `${process.env.REACT_APP_DPOM_OKTA_NONPROD}/v1/authorize`,
    tokenUrl: `${process.env.REACT_APP_DPOM_OKTA_NONPROD}/v1/token`
  },
  prod: {
    // PROD || DR
    issuer: process.env.REACT_APP_DPOM_OKTA_PROD,
    authorizeUrl: `${process.env.REACT_APP_DPOM_OKTA_PROD}/v1/authorize`,
    tokenUrl: `${process.env.REACT_APP_DPOM_OKTA_PROD}/v1/token`
  }
};

async function httpRequestClient(method, url, args) {
  // Create a shallow copy to avoid parameter reassignment
  const requestArgs = { ...args };
  if (
    url.includes('.well-known/openid-configuration')
    || url.includes('token')
  ) {
    requestArgs.withCredentials = false;
    if (requestArgs.headers) {
      // Avoid error if headers is undefined
      delete requestArgs.headers['X-Okta-User-Agent-Extended'];
    }
  }

  if (requestArgs.data) {
    requestArgs.body = requestArgs.data;
    delete requestArgs.data;
  }

  const response = await fetch(url, { method, ...requestArgs });
  const json = await response.json();
  return { responseText: json };
}

// For testing: when set to '1' or 'true',
// tokens are treated as expired after 1 min (for 1-hour tokens)
const isTokenExpireTestMode = () => {
  const val = process.env.REACT_APP_DPOM_TOKEN_EXPIRE_EARLY_FOR_TEST;
  return val === '1' || val === 'true';
};

class DPOMAuth {
  constructor({
    clientId, redirectUri, prod = false, storage = 'localStorage'
  }) {
    const environmentConfig = prod ? config.prod : config.qa;

    const tokenManagerConfig = {
      storage,
      secure: true,
      autoRenew: true,
      syncStorage: false
    };
    if (isTokenExpireTestMode()) {
      tokenManagerConfig.expireEarlySeconds = 3540;
    }

    this[oktaSymbol] = new OktaAuth({
      clientId,
      redirectUri,
      httpRequestClient,
      issuer: environmentConfig.issuer,
      authorizeUrl: environmentConfig.authorizeUrl,
      tokenUrl: environmentConfig.tokenUrl,
      tokenManager: tokenManagerConfig,
      cookies: {
        sameSite: true,
        secure: true
      }
    });
  }

  async initialize() {
    const searchParams = new URLSearchParams(
      window.location.search.substring(1)
    );
    const hasCode = searchParams.get('code');
    if (!hasCode) return false;

    try {
      if (typeof this[oktaSymbol]?.token?.parseFromUrl === 'function') {
        const { tokens } = await this[oktaSymbol].token.parseFromUrl();

        // Ensure tokens have 'value' property for compatibility
        // Okta v7 may store tokens with 'idToken'/'accessToken' but not 'value'
        if (tokens) {
          if (tokens.idToken && !tokens.idToken.value && tokens.idToken.idToken) {
            tokens.idToken.value = tokens.idToken.idToken;
          }
          if (tokens.accessToken && !tokens.accessToken.value && tokens.accessToken.accessToken) {
            tokens.accessToken.value = tokens.accessToken.accessToken;
          }
        }

        this[oktaSymbol].tokenManager.setTokens(tokens);
      } else {
        console.log('Error: Okta token object or parseFromUrl method is not available.');
        return false;
      }
    } catch (error) {
      console.log(`Error: ${error}`);
      return false;
    }

    return true;
  }

  login = async (authorizeOptions = {}) => {
    // Clear any existing auth transactions to prevent conflicts
    // Okta v7 stores transactions with various keys
    try {
      // Clear all localStorage keys that might contain Okta transactions
      const keysToRemove = [];
      Object.keys(localStorage).forEach((key) => {
        if (key.includes('okta') && (key.includes('transaction') || key.includes('shared') || key.includes('pkce'))) {
          keysToRemove.push(key);
        }
      });
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (e) {
      // Ignore errors when clearing
      console.log('Error clearing transaction storage:', e);
    }
    try {
      await this[oktaSymbol].token.getWithRedirect({
        ...authorizeOptions,
        scopes: [
          'openid', 'offline_access', 'email', 'profile', 'legacy_username', 'iam.okta.factoryaffiliations.read', 'iam.okta.factorygroups.read'
        ]
      });
    } catch (error) {
      console.error('Error calling getWithRedirect:', error);
      throw error;
    }
  };

  logout = () => {
    this[oktaSymbol].tokenManager.clear();
  };

  getAccessToken = async () => {
    const token = await this[oktaSymbol].tokenManager.get('accessToken');
    return token;
  };

  /**
   * Renew access token using refresh token.
   * Call this when token is expired (e.g. on 401) - handles tab throttling cases.
   */
  renewAccessToken = async () => {
    const token = await this[oktaSymbol].tokenManager.renew('accessToken');
    return token;
  };

  getIdToken = async () => {
    const token = await this[oktaSymbol].tokenManager.get('idToken');
    return token;
  };

  isLoggedIn = async () => {
    const idToken = await this.getIdToken();
    return idToken ? this[oktaSymbol].token.verify(idToken, {}) : false;
  };

  exists = async () => this[oktaSymbol].session.exists();

  /**
   * Start token manager and auto-renew services.
   * Must be called when user has valid tokens for automatic token refresh to work.
   */
  start = async () => {
    if (this[oktaSymbol] && typeof this[oktaSymbol].start === 'function') {
      await this[oktaSymbol].start();
    }
  };
}

export default DPOMAuth;
